import { roleHasPermission } from '@/auth/permissions';
import { homePathFor } from '@/lib/home-path';
import type { EffectiveRole, StudentAccountKind } from '@/lib/types';
import { NAV, QUICK_ACTIONS, QUICK_ROLE_ORDER, type NavGroup, type NavItem, type QuickAction } from './nav';

/**
 * مصدرٌ واحد لما يراه الدورُ على الجوال (جولة 26 سبتمبر 2026): القائمةُ الكاملة («المزيد»)، وبابا
 * الشريط السفلي، والإجراءاتُ السريعة، واختصاراتُ أيقونة التطبيق (`/go/:intent`). كانت الأولى مكتوبةً
 * داخل `AppShell` والثانيةُ داخل `MobileTabBar` — والاختصاراتُ تحتاجهما معاً، ونسختان تفترقان.
 */
export interface Viewer { roles: readonly EffectiveRole[]; studentKind?: StudentAccountKind | null; quick?: readonly string[] | null }

export function visibleNavGroups(v: Viewer): NavGroup[] {
  const roles = [...v.roles];
  const student = v.studentKind ?? null;
  return NAV
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => {
        if (i.permission && !roleHasPermission(roles, i.permission)) return false;
        if (i.anyRole && !i.anyRole.some((r) => roles.includes(r))) return false;
        if (i.notRole?.some((r) => roles.includes(r))) return false;
        if (i.studentKind && student !== i.studentKind) return false;
        if (i.notStudentKind && student && i.notStudentKind.includes(student)) return false;
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);
}

export function visibleNavItems(v: Viewer): NavItem[] {
  return visibleNavGroups(v).flatMap((g) => g.items);
}

/** أولويةُ الأبواب الأساسية: أولُ اثنين يملكهما المستخدم يصيران تبويبين في الشريط السفلي */
export const PRIMARY: Array<[string, string]> = [
  ['/me/children', 'أبنائي'],
  ['/me/class', 'صفّي'],
  ['/app/attendance', 'الحضور'],
  ['/app/caseload', 'الحالات'],
  ['/app/students', 'الطلبة'],
  ['/app/sessions', 'الجلسات'],
  ['/me/consultations', 'استشاراتي'],
  ['/services', 'الخدمات'],
  ['/platform/accounts', 'الحسابات'],
  ['/platform/institution-requests', 'الطلبات'],
  ['/me/reports', 'التقارير'],
  ['/me/assignments', 'الواجبات'],
  // التقويمُ بعد الواجبات: للمعلّم تبويبا الحضور والواجبات (قرارُ 26 سبتمبر)، والتقويمُ في «المزيد»
  ['/app/schedule', 'تقويمي'],
  ['/app/plans', 'الخطط'],
  ['/app/review', 'المراجعة'],
  ['/me/professional', 'ملفي'],
  ['/academy', 'الأكاديمية'],
];

/** بابا الشريط السفلي لهذا الدور — مما يراه في قائمته أصلاً، ولا يكرّران «الرئيسية» */
export function primaryTabs(items: readonly NavItem[], homePath: string): Array<{ to: string; label: string; item: NavItem }> {
  const have = new Map(items.map((i) => [i.to, i]));
  return PRIMARY
    .filter(([to]) => have.has(to) && to !== homePath
      // الإدارةُ تملك «الطلبة» كلَّهم، و«الحالات» عندها شاشةُ إسنادٍ لا قائمةُ عمل — فلا تأخذ تبويباً
      && !(to === '/app/caseload' && have.has('/app/students'))
      // الحضورُ تبويبٌ للمعلّم وحده (يسجّله كلَّ صباح) — وللإدارة والمختصّ يبقى في «المزيد»
      && !(to === '/app/attendance' && !have.has('/me/class')))
    .slice(0, 2)
    .map(([to, label]) => ({ to, label, item: have.get(to)! }));
}

/** أقصى عددٍ ظاهر من الاختصارات (13 أكتوبر 2026: كان ستة — «أبي حرية للشخص يختار اللي يبيه») */
export const QUICK_MAX = 12;

/**
 * فهرسُ الاختصارات الذي **يختار منه** هذا الشخص (27 سبتمبر 2026): اتحادُ فهارس صفاته كلِّها
 * بترتيب `QUICK_ROLE_ORDER`، مصفّىً بالصلاحية، بلا تكرار. لا اختصارَ خارجه ولا اختصارٌ بلا صلاحية.
 */
export function quickCatalog(roles: readonly EffectiveRole[]): QuickAction[] {
  const seen = new Set<string>();
  const out: QuickAction[] = [];
  for (const r of QUICK_ROLE_ORDER) {
    if (!roles.includes(r)) continue;
    for (const a of QUICK_ACTIONS[r] ?? []) {
      if (seen.has(a.to)) continue;
      if (a.permission && !roleHasPermission([...roles], a.permission)) continue;
      seen.add(a.to); out.push(a);
    }
  }
  return out;
}

/**
 * ما **يختار منه** الشخصُ في «خصّص» (13 أكتوبر 2026 — طلبُ صاحب المنصة): الإجراءاتُ السريعة لصفاته (ومنها أفعالٌ
 * كـ«أضف طفلاً» لا صفحةَ لها في القائمة)، ثم **كلُّ بندٍ في قائمته** كما يراه في «المزيد» — لا فهرسٌ محدود. والبندُ
 * يُعرض باسمه في القائمة (`labelOf` يترجم معرّفَ التسمية). ولا يُضاف ما لا يراه: القائمةُ نفسُها مصفّاةٌ بصلاحياته.
 */
export function menuCatalog(roles: readonly EffectiveRole[], labelOf: (labelId: string) => string): QuickAction[] {
  const seen = new Set<string>();
  const out: QuickAction[] = [];
  for (const a of quickCatalog(roles)) { seen.add(a.to); out.push(a); }
  for (const i of visibleNavItems({ roles, studentKind: null })) {
    if (seen.has(i.to)) continue;
    seen.add(i.to);
    out.push({ to: i.to, labelAr: labelOf(i.labelId), icon: i.icon });
  }
  return out;
}

/**
 * الاختصاراتُ الظاهرة = الفهرس × الصلاحيات × تفضيلُ صاحبها (دالّةٌ نقيّة، مختبَرة).
 *   • بلا تفضيل (`null`): فهرسُ صفته الأولى كما كان، بلا ما يظهر في مكانٍ آخر (`exclude`: الشريطُ
 *     السفلي والرئيسيةُ نفسُها) — فلا يُقترح افتراضياً ما في الشريط.
 *   • بتفضيل: ما اختاره **بترتيبه**، وما فقد صلاحيتَه أو خرج من الفهرس يسقط صامتاً؛ ولا يُستبعد ما في
 *     الشريط — اختارَه هو. والحدُّ ستة.
 */
export function quickActionsFor(roles: readonly EffectiveRole[], exclude: ReadonlySet<string> = new Set(), chosen: readonly string[] | null = null, catalog: readonly QuickAction[] = quickCatalog(roles)): QuickAction[] {
  if (chosen) {
    const byKey = new Map(catalog.map((a) => [a.to, a]));
    return chosen.map((k) => byKey.get(k)).filter((a): a is QuickAction => !!a).slice(0, QUICK_MAX);
  }
  const role = QUICK_ROLE_ORDER.find((r) => roles.includes(r));
  if (!role) return [];
  return (QUICK_ACTIONS[role] ?? [])
    .filter((a) => !a.permission || roleHasPermission([...roles], a.permission))
    // مقارنةٌ بالهدف كاملاً: «أضف طفلاً» (`/me/children?add=1`) فعلٌ غيرُ الصفحة التي يفتحها
    .filter((a) => !exclude.has(a.to))
    .slice(0, QUICK_MAX);
}

export type Intent = 'inbox' | 'primary' | 'quick';

/**
 * نيّةُ اختصار الأيقونة → مسارٌ لهذا الدور. «التنبيهات» لكل من له صندوق، و«بابي» أولُ بابٍ في
 * الشريط السفلي، و«إجراء سريع» أولُ إجراءٍ يملكه. والمجهولةُ — أو ما لا يملكه — الرئيسيةُ.
 */
export function resolveIntent(intent: string, v: Viewer): string {
  const home = homePathFor(v.roles);
  const items = visibleNavItems(v);
  switch (intent) {
    case 'inbox': return items.some((i) => i.to === '/me/notifications') ? '/me/notifications' : home;
    case 'primary': return primaryTabs(items, home)[0]?.to ?? home;
    // «إجراء سريع» = أولُ ما اختاره صاحبُه إن خصّص، وإلا أولُ الافتراضي
    case 'quick': return quickActionsFor(v.roles, new Set([home]), v.quick ?? null)[0]?.to ?? home;
    default: return home;
  }
}
